import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Activity, Brain, ShieldCheck, Zap, Heart, Plug, MessageCircle, ArrowRight, Mail, CheckCircle2, Building2, Globe, Server } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-sky-600 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>engagingly.ai</span>
              <p className="text-[9px] text-slate-400 leading-tight -mt-0.5">Where health meets intelligent engagement</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="mailto:sales@cspsolutions.com" className="text-sm text-slate-600 hover:text-sky-600 transition-colors hidden sm:block">Contact Sales</a>
            <Button data-testid="nav-login-btn" onClick={() => navigate('/login')} variant="outline" size="sm">Sign In</Button>
            <Button data-testid="nav-demo-btn" onClick={() => window.location.href = 'mailto:sales@cspsolutions.com?subject=engagingly.ai%20Demo%20Request'} size="sm" className="bg-sky-600 hover:bg-sky-700">Request Demo</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div>
              <span className="inline-block px-3 py-1 bg-sky-50 text-sky-700 text-xs font-semibold rounded-full mb-6 border border-sky-100">AI Health Execution Layer</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Better Patient Care Through <span className="text-sky-600">Intelligent Engagement</span>
              </h1>
            </div>
            <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
              engagingly.ai helps hospitals, clinics, and medical groups engage with patients at every touchpoint &mdash; from AI-powered triage to automated follow-ups &mdash; ensuring every patient is healthy, heard, and cared for.
            </p>
            <div className="flex items-center gap-4">
              <Button data-testid="hero-demo-btn" onClick={() => window.location.href = 'mailto:sales@cspsolutions.com?subject=engagingly.ai%20Free%20Trial%20Request&body=Hi%2C%20I%20am%20interested%20in%20starting%20a%20free%20trial%20of%20engagingly.ai.%20Please%20contact%20me.'} className="h-12 px-8 bg-sky-600 hover:bg-sky-700 text-base">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a href="mailto:sales@cspsolutions.com" className="h-12 px-6 inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-sky-600 border border-slate-200 rounded-lg hover:border-sky-200 transition-all">
                <Mail className="w-4 h-4" /> Contact Sales
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-3xl p-8 lg:p-10 border border-sky-100">
              <div className="space-y-4">
                {[
                  { icon: Brain, title: 'AI Triage', desc: 'Intelligent symptom assessment with medical guardrails' },
                  { icon: Heart, title: 'Patient Follow-Ups', desc: 'Automated post-care check-ins via SMS, WhatsApp & Email' },
                  { icon: Plug, title: 'EHR Integration', desc: 'Connect with 22+ healthcare systems using HL7 & FHIR' },
                  { icon: MessageCircle, title: 'Omnichannel', desc: 'Web chat, WhatsApp, SMS, and embeddable widgets' },
                ].map(({ icon: Icon, title, desc }, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100 shadow-sm animate-fade-in" style={{ animationDelay: `${i * 120}ms` }}>
                    <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it helps */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>How engagingly.ai Helps Hospitals</h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto text-base">A complete AI-powered patient engagement platform that ensures no patient is left behind</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Brain, title: 'Intelligent Triage', desc: 'AI collects symptoms, assesses urgency, and routes patients to the right specialist. Reduces wait times and ensures critical cases get immediate attention.', color: 'sky' },
              { icon: Heart, title: 'Continuous Care', desc: 'Automated follow-ups check on patients after appointments. Track recovery, collect feedback, and detect early warning signs before they become emergencies.', color: 'emerald' },
              { icon: ShieldCheck, title: 'HIPAA-Ready', desc: 'Full audit trail, encrypted data, consent tracking, and role-based access control. Designed from the ground up for healthcare compliance.', color: 'purple' },
              { icon: Zap, title: 'Workflow Automation', desc: 'Pre-visit forms, appointment booking, patient journey tracking, and document management &mdash; all automated to reduce staff burden.', color: 'amber' },
              { icon: MessageCircle, title: 'Meet Patients Where They Are', desc: 'Communicate via web chat, WhatsApp, SMS, and email. Embeddable widgets let patients start triage directly from your website.', color: 'rose' },
              { icon: Building2, title: 'Multi-Facility Support', desc: 'Multi-tenant architecture supports hospitals, polyclinics, and medical groups. Each facility gets its own workspace with white-label branding.', color: 'indigo' },
            ].map(({ icon: Icon, title, desc, color }, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-sky-200 hover:shadow-lg transition-all group">
                <div className={`w-12 h-12 rounded-xl bg-${color}-50 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-6 h-6 text-${color}-600`} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed" dangerouslySetInnerHTML={{ __html: desc }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-100">Universal Healthcare Integration</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                An AI Execution Layer That Speaks Every Healthcare Language
              </h2>
              <p className="text-slate-600 leading-relaxed">
                engagingly.ai is an <strong>AI Health Execution Layer</strong> that communicates with any healthcare software using <strong>HL7</strong> or <strong>FHIR</strong> protocols. Whether your hospital runs Epic, Cerner, InterSystems TrakCare, or regional systems like Malaffi, NABIDH, or BestCare &mdash; we integrate seamlessly.
              </p>
              <div className="space-y-3">
                {[
                  { icon: Globe, text: 'Works with 22+ EHR/EMR systems worldwide' },
                  { icon: Server, text: 'Deploy on-premise or on private cloud' },
                  { icon: Plug, text: 'HL7 v2.x, FHIR R4, CDA, and REST API support' },
                  { icon: ShieldCheck, text: 'Pull patients, doctors, and appointments from any system' },
                ].map(({ icon: Icon, text }, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-sm text-slate-700">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8 text-white">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-4 font-semibold">Supported Systems</p>
              <div className="flex flex-wrap gap-2">
                {['Epic', 'Cerner', 'Meditech', 'Allscripts', 'eClinicalWorks', 'InterSystems', 'NextGen', 'SAP Healthcare', 'Malaffi', 'NABIDH', 'Riayati', 'Wareed', 'Hakeem', 'HealthLink', 'BestCare', 'Nana EMR', 'TebCare', 'Royal EMR', 'Vista', 'OmniMD', 'Quadris', 'Salamat'].map((s, i) => (
                  <span key={i} className="px-3 py-1.5 bg-white/10 rounded-lg text-xs font-medium text-slate-200 border border-white/5">{s}</span>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">On-Premise Deployment</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">Private Cloud Ready (AWS, Azure, GCP)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">Kubernetes & Docker Containerized</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-sky-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Ready to Transform Patient Engagement?</h2>
          <p className="text-sky-100 text-lg mb-8 max-w-xl mx-auto">
            Join hospitals and clinics that use engagingly.ai to deliver better patient care through intelligent automation.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button data-testid="cta-demo-btn" onClick={() => window.location.href = 'mailto:sales@cspsolutions.com?subject=engagingly.ai%20Free%20Trial%20Request&body=Hi%2C%20I%20am%20interested%20in%20starting%20a%20free%20trial%20of%20engagingly.ai.%20Please%20contact%20me.'} className="h-12 px-8 bg-white text-sky-700 hover:bg-sky-50 text-base font-semibold">
              Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href="mailto:sales@cspsolutions.com" className="h-12 px-8 inline-flex items-center gap-2 text-white border-2 border-white/40 rounded-lg hover:bg-white/10 transition-all font-medium">
              <Mail className="w-4 h-4" /> sales@cspsolutions.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="text-white font-bold" style={{ fontFamily: 'Manrope' }}>engagingly.ai</span>
              <p className="text-[10px] text-slate-500">Where health meets intelligent engagement</p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <a href="https://engagingly.ai/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Website</a>
            <a href="mailto:sales@cspsolutions.com" className="hover:text-white transition-colors">Contact Sales</a>
            <span>sales@cspsolutions.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
